// VERSION 2.2 - Implementierung Spielmodi
// =============================================================================
//  SCREEN: TabletScoreScreen (Angepasst für flexible Spielmodi und Sätze)
// =============================================================================
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import { toast } from 'react-toastify';
// NEU: Import der umbenannten und refaktorisierten Komponente
import PlayerScoreEntry from '../components/PlayerScoreEntry';
import Numpad from '../components/Numpad';

// NEU: Definition der Spielmodi, um die Anzahl der Sätze zu steuern.
// Dies ist erweiterbar für zukünftige Spielmodi.
const GAME_MODES = {
  'CLASSIC_120': { sets: 4, label: '120 Wurf Classic' },
  '100_WURF': { sets: 2, label: '100 Wurf' },
  '200_WURF': { sets: 4, label: '200 Wurf' },
  'SPRINT': { sets: 2, label: 'Sprint' },
  // Weitere Modi können hier hinzugefügt werden
};

// NEU: Eine Hilfsfunktion, um leere Score-Daten für einen Spielmodus zu erstellen.
const initializeScoreData = (gameMode) => {
  const mode = GAME_MODES[gameMode];
  if (!mode) return { sets: [] }; // Fallback
  return {
    gameMode,
    phase: 'qualification',
    sets: Array.from({ length: mode.sets }, () => ({ volle: '', abr: '', fw: '' })),
  };
};


const TabletScoreScreen = () => {
  const { id: tournamentId } = useParams();

  const [tournament, setTournament] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [allEntries, setAllEntries] = useState({});
  const [allScores, setAllScores] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [selectedLanes, setSelectedLanes] = useState(() => {
    const saved = localStorage.getItem(`tabletLanes_${tournamentId}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeslot, setSelectedTimeslot] = useState('');
  // GEÄNDERT: Die Struktur von activeInput wurde an das Satz-Modell angepasst.
  const [activeInput, setActiveInput] = useState(null); // Shape: { entryId, setIndex, field }
  const [lastModifiedEntryId, setLastModifiedEntryId] = useState(null);
  const autosaveTimeout = useRef(null);
  
  // NEU: Aktueller Spielmodus. Vorerst hardcodiert, später über Turnier-Einstellungen wählbar.
  const [currentGameMode] = useState('CLASSIC_120');

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, scheduleRes, entriesRes, scoresRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/schedule`),
          apiClient.get(`/api/tournaments/${tournamentId}/entries`),
          apiClient.get(`/api/tournaments/${tournamentId}/scores`),
        ]);
        setTournament(tournamentRes.data);
        setSchedule(scheduleRes.data);
        setAllEntries(entriesRes.data.reduce((acc, entry) => { acc[entry._id] = entry; return acc; }, {}));
        const scoresMap = scoresRes.data.reduce((acc, score) => {
          // GEÄNDERT: Schlüssel ist jetzt komplexer, aber für die Quali reicht die Entry-ID
          if (score.entry?._id) acc[score.entry._id] = score;
          return acc;
        }, {});
        setAllScores(scoresMap);
      } catch (err) {
        toast.error("Fehler beim Laden der Turnierdaten.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [tournamentId]);
  
  // GEÄNDERT: Die Autosave-Logik sendet nun Daten im neuen, vom Backend erwarteten Format.
  useEffect(() => {
    if (!lastModifiedEntryId) return;
    if (autosaveTimeout.current) clearTimeout(autosaveTimeout.current);
    
    autosaveTimeout.current = setTimeout(async () => {
      const scoreToSave = allScores[lastModifiedEntryId];
      if (!scoreToSave || !scoreToSave.sets) return;

      // Bereite die Daten für das Backend vor (konvertiere leere Strings zu 0)
      const sanitizedSets = scoreToSave.sets.map(set => ({
        volle: Number(set.volle) || 0,
        abr: Number(set.abr) || 0,
        fw: Number(set.fw) || 0,
      }));

      try {
        const payload = {
          entryId: lastModifiedEntryId,
          tournamentId,
          gameMode: scoreToSave.gameMode || currentGameMode, // Nimm den Modus aus dem Score oder den aktuellen
          phase: scoreToSave.phase || 'qualification',
          sets: sanitizedSets,
        };
        const { data: savedScore } = await apiClient.post('/api/scores', payload);
        
        setAllScores(prev => ({ ...prev, [lastModifiedEntryId]: savedScore }));
        toast.success(`Ergebnis für ${savedScore.entry.player.firstName} automatisch gespeichert!`);
        setLastModifiedEntryId(null);
      } catch (err) {
        toast.error("Automatisches Speichern fehlgeschlagen.");
      }
    }, 2000);

    return () => {
      if (autosaveTimeout.current) clearTimeout(autosaveTimeout.current);
    };
  }, [allScores, lastModifiedEntryId, tournamentId, currentGameMode]);

  const handleSelectLanes = (lanes) => {
    if (lanes.length > 0) {
      setSelectedLanes(lanes);
      localStorage.setItem(`tabletLanes_${tournamentId}`, JSON.stringify(lanes));
    } else {
      setSelectedLanes([]);
      localStorage.removeItem(`tabletLanes_${tournamentId}`);
    }
  };
  
  const { availableDates, timeslotsByDate } = useMemo(() => {
    if (!schedule) return { availableDates: [], timeslotsByDate: {} };
    const now = new Date();
    const cutoffTime = now.getTime() - (15 * 60 * 1000); 
    const dateMap = {};
    schedule.forEach(s => {
      const startTime = new Date(s.startTime);
      if (startTime.getTime() < cutoffTime) return;
      const dateKey = startTime.toISOString().split('T')[0];
      if (!dateMap[dateKey]) dateMap[dateKey] = new Set();
      dateMap[dateKey].add(s.startTime);
    });
    const sortedDates = Object.keys(dateMap).sort();
    sortedDates.forEach(dateKey => { dateMap[dateKey] = Array.from(dateMap[dateKey]).sort(); });
    return { availableDates: sortedDates, timeslotsByDate: dateMap };
  }, [schedule]);

  const activeScheduleEntries = useMemo(() => {
    if (selectedLanes.length === 0 || !selectedTimeslot || Object.keys(allEntries).length === 0) return {};
    const active = {};
    const slotsForTime = schedule.filter(s => s.startTime === selectedTimeslot);
    selectedLanes.forEach(laneNumber => {
      const scheduleSlot = slotsForTime.find(s => s.startLane === laneNumber);
      active[laneNumber] = (scheduleSlot && allEntries[scheduleSlot.entry?._id]) ? { ...scheduleSlot, entry: allEntries[scheduleSlot.entry._id] } : null;
    });
    return active;
  }, [selectedTimeslot, schedule, selectedLanes, allEntries]);

  // GEÄNDERT: Die Tab-Reihenfolge wird nun basierend auf Sätzen statt Bahnen berechnet.
  const tabOrder = useMemo(() => {
    const order = [];
    if (!activeScheduleEntries) return order;
    
    const numberOfSets = GAME_MODES[currentGameMode]?.sets || 0;
    const sortedActiveLanes = Object.values(activeScheduleEntries).filter(Boolean).sort((a, b) => a.startLane - b.startLane);

    sortedActiveLanes.forEach(scheduleEntry => {
      const entryId = scheduleEntry.entry._id;
      for (let i = 0; i < numberOfSets; i++) {
        order.push({ entryId, setIndex: i, field: 'volle' });
        order.push({ entryId, setIndex: i, field: 'abr' });
        order.push({ entryId, setIndex: i, field: 'fw' });
      }
    });
    return order;
  }, [activeScheduleEntries, currentGameMode]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Tab') { e.preventDefault(); handleTab(e.shiftKey ? -1 : 1); }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeInput, tabOrder]); // Abhängigkeiten sind wichtig, damit immer der aktuelle Stand verwendet wird.

  const handleTab = (direction) => {
    if (tabOrder.length === 0) return;
    if (!activeInput) {
      setActiveInput(tabOrder[0]);
      return;
    }
    const currentIndex = tabOrder.findIndex(item => 
      item.entryId === activeInput.entryId &&
      item.setIndex === activeInput.setIndex &&
      item.field === activeInput.field
    );
    let nextIndex = (currentIndex + direction + tabOrder.length) % tabOrder.length;
    setActiveInput(tabOrder[nextIndex]);
  };
  
  // GEÄNDERT: `handleScoreChange` zu `handleSetChange` für Klarheit. Aktualisiert das `sets`-Array.
  const handleSetChange = (entryId, setIndex, field, value) => {
    setAllScores(prev => {
      const currentScore = prev[entryId] || initializeScoreData(currentGameMode);
      const newSets = [...currentScore.sets];
      newSets[setIndex] = { ...newSets[setIndex], [field]: value };
      return { ...prev, [entryId]: { ...currentScore, sets: newSets, entry: { _id: entryId } } };
    });
    setLastModifiedEntryId(entryId);
  };

  const handleNumpadKeyPress = (key) => {
    if (!activeInput) return;
    const { entryId, setIndex, field } = activeInput;
    const currentScore = allScores[entryId] || initializeScoreData(currentGameMode);
    const currentValue = (currentScore.sets[setIndex]?.[field] ?? '').toString();
    if (currentValue.length >= 3) return;
    const newValue = parseInt(currentValue + key, 10);
    handleSetChange(entryId, setIndex, field, newValue);
  };

  const handleNumpadBackspace = () => {
    if (!activeInput) return;
    const { entryId, setIndex, field } = activeInput;
    const currentScore = allScores[entryId] || initializeScoreData(currentGameMode);
    const currentValue = (currentScore.sets[setIndex]?.[field] ?? '').toString();
    const newValue = currentValue.slice(0, -1);
    handleSetChange(entryId, setIndex, field, newValue === '' ? '' : parseInt(newValue, 10));
  };

  const handleNumpadClear = () => {
    if (!activeInput) return;
    const { entryId, setIndex, field } = activeInput;
    handleSetChange(entryId, setIndex, field, '');
  };

  const formatDateForDisplay = (isoDateString) => new Date(isoDateString).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  const formatTimeForDisplay = (isoString) => new Date(isoString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });

  if (loading) return <div className="tablet-setup-container"><h1>Lade Daten...</h1></div>;

  if (selectedLanes.length === 0) {
    return (
      <div className="tablet-setup-container">
        <h1>Tablet-Modus Konfiguration</h1>
        <p>Wählen Sie die Bahnen aus, die auf diesem Gerät erfasst werden sollen.</p>
        <div className="lane-selection-buttons">
          <button onClick={() => handleSelectLanes([1, 2])}>Bahnen 1 & 2</button>
          <button onClick={() => handleSelectLanes([3, 4])}>Bahnen 3 & 4</button>
          {tournament?.numberOfLanes > 4 && <button onClick={() => handleSelectLanes([5, 6])}>Bahnen 5 & 6</button>}
          {tournament?.numberOfLanes > 6 && <button onClick={() => handleSelectLanes([7, 8])}>Bahnen 7 & 8</button>}
          <button onClick={() => handleSelectLanes(Array.from({ length: tournament?.numberOfLanes || 4 }, (_, i) => i + 1))}>Alle Bahnen</button>
        </div>
        <Link to={`/turnier/${tournamentId}/startplan`} className="btn-back" style={{marginTop: '2rem'}}>Zurück zum Startplan</Link>
      </div>
    );
  }

  return (
    <div className="tablet-view-container">
      <div className="tablet-header">
        <h2>{tournament?.name} - Bahnen: {selectedLanes.join(', ')}</h2>
        <button onClick={() => handleSelectLanes([])} className="btn-secondary-action">Bahnen ändern</button>
      </div>
      <div className="timeslot-selector-container">
        <div className="timeslot-group">
          <label htmlFor="date-select">Tag auswählen:</label>
          <select id="date-select" value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setSelectedTimeslot(''); }}>
            <option value="">-- Bitte Tag wählen --</option>
            {availableDates.map(date => <option key={date} value={date}>{formatDateForDisplay(date)}</option>)}
          </select>
        </div>
        <div className="timeslot-group">
          <label htmlFor="timeslot-select">Durchgang auswählen:</label>
          <select id="timeslot-select" value={selectedTimeslot} onChange={(e) => setSelectedTimeslot(e.target.value)} disabled={!selectedDate}>
            <option value="">-- Bitte Zeit wählen --</option>
            {selectedDate && timeslotsByDate[selectedDate]?.map(time => <option key={time} value={time}>{formatTimeForDisplay(time)} Uhr</option>)}
          </select>
        </div>
      </div>
      <div className="tablet-main-content">
        <div className="tablet-lanes-grid">
          {selectedLanes.map(laneNumber => {
            const scheduleEntry = activeScheduleEntries[laneNumber];
            const entry = scheduleEntry?.entry;
            const scoreData = (entry && allScores[entry._id]) ? allScores[entry._id] : initializeScoreData(currentGameMode);
            
            return (
              <div key={laneNumber} className="lane-container">
                <h3>Bahn {laneNumber}</h3>
                <div className="active-player-info">
                  {entry?.player ? (
                    <>
                      <span className="player-name">{entry.player.firstName} {entry.player.lastName}</span>
                      <span className="player-affiliation">{entry.team?.name || entry.player.club?.name || 'Vereinslos'}</span>
                    </>
                  ) : (
                    <span className="no-player">{selectedTimeslot ? 'Bahn frei' : 'Wähle Durchgang'}</span>
                  )}
                </div>
                {entry && (
                  // GEÄNDERT: Die neue Komponente wird hier mit den neuen Props verwendet.
                  <PlayerScoreEntry
                    sets={scoreData.sets}
                    onFocusChange={(focusData) => setActiveInput({ entryId: entry._id, ...focusData })}
                    onSetChange={(setIndex, field, value) => handleSetChange(entry._id, setIndex, field, value)}
                    activeInput={activeInput?.entryId === entry._id ? activeInput : null}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="numpad-container-central">
          <Numpad onKeyPress={handleNumpadKeyPress} onBackspace={handleNumpadBackspace} onClear={handleNumpadClear} onNext={() => handleTab(1)} onPrev={() => handleTab(-1)} />
        </div>
      </div>
    </div>
  );
};

export default TabletScoreScreen;